import dashboardScreenshot from "../assets/docs-screenshots/dashboard.webp"
import siteCampaignsScreenshot from "../assets/docs-screenshots/site-campaigns.webp"
import siteSegmentsScreenshot from "../assets/docs-screenshots/site-segments.webp"
import siteSetupScreenshot from "../assets/docs-screenshots/site-setup.webp"
import siteTrackingScreenshot from "../assets/docs-screenshots/site-tracking.webp"
import { Link } from "../components/link"
import { SiteHeader } from "../components/site-header"

type DocsTopic = {
  slug: string
  eyebrow: string
  title: string
  intro: string
  screenshot: {
    src: string
    alt: string
    caption: string
  }
  sections: Array<{
    title: string
    body: string[]
    example: string
  }>
}

const docsTopics: DocsTopic[] = [
  {
    slug: "first-session",
    eyebrow: "Start here",
    title: "From website to campaign launch",
    intro:
      "Adgiro gives a buyer the full launch path in one workspace: site intake, account connections, website tag installation, creative generation, campaign generation, conversion actions, lead reporting, and optimization.",
    screenshot: {
      src: dashboardScreenshot,
      alt: "Adgiro dashboard with workspace, tracked site, active campaign, primary conversions, spend, revenue, and optimization summary.",
      caption:
        "The dashboard shows the operating view: connected channels, tracked sites, active campaigns, primary conversions, spend, revenue, and recent optimization work.",
    },
    sections: [
      {
        title: "Add the site and create the brief",
        body: [
          "The website becomes the campaign source of truth. Adgiro reads the homepage, offer pages, pricing pages, documentation, landing pages, proof points, calls to action, and media, then turns that material into campaign context.",
          "This is the same evaluation flow buyers expect from advertising platforms: define the website, identify the conversion goal, choose the landing page, prepare creative, and review the launch plan before spend moves.",
        ],
        example:
          "Example: a service business adds its domain and Adgiro identifies the quote form, service-area pages, testimonials, calls to action, and images that can support search and social campaigns.",
      },
      {
        title: "Connect Google, Meta, and measurement",
        body: [
          "Adgiro connects the ad accounts and measurement sources that campaign systems use every day. Google Ads, Analytics, Search Console, Meta accounts, website events, and conversion mappings stay attached to the site workspace.",
          "The Adgiro tag works like a sitewide website tag or pixel. It gives the workspace a reliable feed of page views, clicks, form activity, and lead journeys so reporting and optimization have real customer behavior to use.",
        ],
        example:
          "Example: a buyer can install the tag through a site template or tag manager, then confirm that page views and form submissions are flowing into the tracking feed.",
      },
      {
        title: "Generate, review, and launch",
        body: [
          "Adgiro prepares channel-ready campaign materials from the website: search intent, excluded search terms, ad copy, creative angles, landing page choices, budget guardrails, and conversion actions.",
          "The review path is built for confidence before launch. A customer can see what will run, which landing pages will receive traffic, how conversion events are defined, and how the account will improve after leads arrive.",
        ],
        example:
          "Example: a SaaS company can review Google Search copy for demo intent, Meta creative angles around product screenshots, and a primary conversion action for qualified demo requests.",
      },
    ],
  },
  {
    slug: "workspace",
    eyebrow: "Workspace map",
    title: "A workspace built around the site",
    intro:
      "Adgiro organizes campaign work around the website because the site contains the offer, landing pages, media, conversion paths, and customer proof that advertising needs.",
    screenshot: {
      src: siteSegmentsScreenshot,
      alt: "Adgiro site workspace with segments, setup, tracking, campaigns, analytics, asset totals, landing pages, copy assets, and media assets.",
      caption:
        "The site workspace groups landing pages, targeting inputs, copy, media, setup, tracking, campaigns, and analytics under the same business context.",
    },
    sections: [
      {
        title: "Dashboard",
        body: [
          "The dashboard is the account overview. It shows the workspace, tracked sites, active campaigns, primary conversions, spend, revenue, and optimization activity in one place.",
          "For someone evaluating Adgiro, the dashboard explains the operating model clearly. The product manages connected ad accounts, site-level measurement, campaign activity, lead outcomes, and ongoing optimization.",
        ],
        example:
          "Example: a growth team can use the dashboard for a weekly check-in, review conversion volume and spend, then open the site that needs campaign or creative work.",
      },
      {
        title: "Sites and segments",
        body: [
          "The site page is where the work happens. Segments group the pages and assets Adgiro found, including landing pages, targeting context, generated copy, and captured media.",
          "Segments make the campaign plan easier to review because the buyer can see which site material will shape a campaign. A default segment can support the first launch, and additional segments can support product lines, locations, audiences, or seasonal offers.",
        ],
        example:
          "Example: a multi-location business can create segments for each market, attach location-specific landing pages, and generate ads with the right local proof.",
      },
      {
        title: "Accounts and controls",
        body: [
          "Accounts connect the workspace to provider destinations. A Google connection can support Ads, Analytics, and Search Console workflows; a Meta connection can support campaign and lead-generation destinations.",
          "Controls keep the workspace ready for larger teams. Adgiro can support account assignment, budget guardrails, approval flows, launch history, and team governance as the customer grows.",
        ],
        example:
          "Example: one operator can link provider accounts once, assign the right accounts to each site, and keep campaign generation tied to approved destinations.",
      },
    ],
  },
  {
    slug: "site-setup",
    eyebrow: "Site setup",
    title: "Turn a website into campaign context",
    intro:
      "Site setup shows what Adgiro has learned from the business and what needs to be connected before campaigns launch: accounts, measurement, crawl status, source pages, and reusable assets.",
    screenshot: {
      src: siteSetupScreenshot,
      alt: "Adgiro setup tab with connected accounts, analytics measurement, search console, and site crawl controls.",
      caption:
        "Setup brings provider accounts, measurement, and crawl status together before assets and campaigns are generated.",
    },
    sections: [
      {
        title: "What Adgiro reads",
        body: [
          "Adgiro reads public pages to understand what the business sells, who the offer serves, what proof supports the offer, and which calls to action matter.",
          "The crawl collects useful marketing material: homepage positioning, product and service pages, pricing pages, docs, landing pages, calls to action, signup paths, demo paths, quote paths, contact paths, brand colors, images, and videos.",
        ],
        example:
          "Example: a B2B site can produce campaign context from feature pages, security pages, customer logos, screenshots, pricing language, and demo calls to action.",
      },
      {
        title: "What the customer reviews",
        body: [
          "The review experience presents the main offer, audience context, page groups, captured media, landing pages, and measurement status in plain language.",
          "This gives a buyer the same core answers they would seek during a campaign setup call: where traffic will go, which action counts as valuable, what creative can be used, and how results will be measured.",
        ],
        example:
          "Example: a buyer reviewing a quote campaign can confirm that traffic points to pages with service details, trust signals, and quote forms.",
      },
      {
        title: "What setup unlocks",
        body: [
          "Once accounts and measurement are ready, Adgiro can generate assets, draft campaign plans, map conversion events, and prepare launch decisions.",
          "Setup also creates a clear maintenance path. When the site changes, the workspace can refresh source material and use the updated pages or media for new campaign work.",
        ],
        example:
          "Example: when a company publishes a new product page, Adgiro can use it for new landing page recommendations, ad copy, creative angles, and targeting context.",
      },
    ],
  },
  {
    slug: "tracking",
    eyebrow: "Tracking",
    title: "Website events and lead reporting",
    intro:
      "The Adgiro tag turns customer behavior into a lead-generation feed. Page views, clicks, form activity, journeys, conversion mappings, and provider events become visible in the workspace.",
    screenshot: {
      src: siteTrackingScreenshot,
      alt: "Adgiro tracking tab with install script, realtime event summary, conversion mappings, GA4 event, and Meta event controls.",
      caption:
        "Tracking includes the install script, realtime checks, conversion mappings, GA4 event names, Meta event names, and captured journey summaries.",
    },
    sections: [
      {
        title: "What the tag captures",
        body: [
          "The tag captures the signals buyers expect from modern ad systems: page views, CTA clicks, link clicks, form starts, form submissions, signup flows, demo requests, quote requests, contact requests, onboarding steps, and lead-generation journeys.",
          "Those events give Adgiro the data needed for conversion reporting, audience signals, optimization goals, and channel-specific event forwarding.",
        ],
        example:
          "Example: a visitor lands on a service page, clicks Get a Quote, starts the form, submits it, and reaches the thank-you page. Adgiro can summarize that path as a lead journey.",
      },
      {
        title: "How mappings work",
        body: [
          "Mappings turn observed customer actions into conversion events. A pricing view can become an intent signal, a contact form can become a lead event, and a demo request can become the primary conversion action for optimization.",
          "Adgiro keeps the mapping language familiar across providers. GA4 events, Google conversion actions, Meta standard events such as Lead, and custom event names can all be represented from the same site behavior.",
        ],
        example:
          "Example: a demo submit can map to a GA4 event, a Google Ads conversion action, and a Meta Lead event so reporting and optimization share the same business outcome.",
      },
      {
        title: "How customers use the feed",
        body: [
          "The tracking feed helps customers verify installation, understand customer journeys, and see which actions campaigns should optimize toward.",
          "It also helps Adgiro improve over time. Lead quality, conversion volume, campaign spend, creative performance, and mapped events become the operating data behind budget and campaign recommendations.",
        ],
        example:
          "Example: after several high-quality quote requests arrive, a customer can treat quote submissions as the primary event and use pricing-page visits as a secondary intent signal.",
      },
    ],
  },
  {
    slug: "campaigns",
    eyebrow: "Campaigns",
    title: "Campaign review and launch controls",
    intro:
      "Campaigns are presented as a launch plan: objective, channel, landing page, audience, creative, budget, conversion action, approval state, and optimization goal.",
    screenshot: {
      src: siteCampaignsScreenshot,
      alt: "Adgiro campaigns tab in the site workspace.",
      caption:
        "Campaign work starts from selected site segments so channel plans stay connected to the pages, media, copy, and conversion signals that shaped them.",
    },
    sections: [
      {
        title: "What Adgiro prepares",
        body: [
          "Adgiro packages site context and generated assets into campaign-ready work. The output can include search intent, excluded search terms, channel inputs, creative copy, image angles, landing page selections, and conversion actions.",
          "This language matches the way buyers evaluate ad platforms: objective, audience, budget, creative, placement, landing page, conversion event, launch status, and reporting.",
        ],
        example:
          "Example: a lead-generation launch can include search ad groups for high-intent terms, Meta creative angles around proof, and a primary conversion action for qualified leads.",
      },
      {
        title: "What strong output includes",
        body: [
          "Strong campaign output is specific to the business. It uses the offer, the customer language, the landing page, the proof, and the conversion event rather than generic campaign filler.",
          "Adgiro can use real site media, product screenshots, service pages, testimonials, pricing context, and calls to action to produce campaign copy and creative directions that fit the brand.",
        ],
        example:
          "Example: a captured product screenshot can support a Meta creative concept, while the demo page and customer proof shape search descriptions and sitelink ideas.",
      },
      {
        title: "Budgets, approvals, and optimization",
        body: [
          "Budget guardrails give buyers control over spend and pacing. Approval flows keep larger changes visible while routine optimization can continue inside the rules the customer sets.",
          "As performance data arrives, Adgiro can use conversion volume, lead quality, channel performance, and creative performance to recommend budget movement, targeting changes, and creative refreshes.",
        ],
        example:
          "Example: a customer can start with a daily budget cap, require approval above a threshold, and scale campaigns after lead quality and conversion volume support more spend.",
      },
    ],
  },
  {
    slug: "scale",
    eyebrow: "Scale",
    title: "Start self-serve and grow into managed control",
    intro:
      "Adgiro is built for founders, startups, and growing teams that want serious advertising infrastructure before they reach enterprise spend levels.",
    screenshot: {
      src: dashboardScreenshot,
      alt: "Adgiro dashboard with active campaign metrics and optimization summary.",
      caption:
        "The same workspace can support first launch, weekly optimization, team review, and stronger account governance as the customer grows.",
    },
    sections: [
      {
        title: "Self-serve launch",
        body: [
          "A customer can begin with one site, connected accounts, a website tag, generated assets, generated campaigns, and a clear review path for launch.",
          "This makes the product easy to evaluate before buying. Buyers can see what the workspace provides, how campaign material is generated, how tracking works, and how optimization is reported.",
        ],
        example:
          "Example: a founder can add a domain, connect Google and Meta, install the tag, review generated campaign material, and launch with a budget guardrail.",
      },
      {
        title: "Growth team workflow",
        body: [
          "As the business adds spend and complexity, the workspace can support more sites, more creative refreshes, more campaign plans, more lead feedback, and more approval rules.",
          "The operating rhythm stays clear: refresh site context, generate or review assets, update campaigns, watch lead events, and use optimization history to decide what changes next.",
        ],
        example:
          "Example: a growth team can review weekly conversion volume, approve new creative angles, increase budget on strong campaigns, and keep the action history attached to the site.",
      },
      {
        title: "Enterprise controls",
        body: [
          "Larger teams can add stronger identity, governance, account assignment, multi-site structure, launch approvals, and budget oversight while keeping the same site-centered workflow.",
          "This gives customers a path from first campaign to larger managed programs with consistent measurement, reporting, and optimization language.",
        ],
        example:
          "Example: a multi-brand operator can govern several sites, map provider accounts to each site, require approval for budget changes, and review reporting by workspace.",
      },
    ],
  },
]

const topicLinks = docsTopics.map((topic) => ({
  href: `/docs/${topic.slug}`,
  label: topic.title,
  eyebrow: topic.eyebrow,
}))

export function DocsPage({ topic }: { topic?: string }) {
  const selected = docsTopics.find((item) => item.slug === topic)
  return (
    <div className="site-frame">
      <SiteHeader />
      {selected ? <DocsTopicPage topic={selected} /> : <DocsHub />}
    </div>
  )
}

function DocsHub() {
  return (
    <main className="simple-page docs-page">
      <div className="eyebrow">Product guide</div>
      <h1>Adgiro docs for campaign growth</h1>
      <p className="docs-intro">
        Use these docs to evaluate what Adgiro provides: website intake, account connections,
        website tag installation, conversion events, creative assets, Google and Meta campaign
        generation, budget guardrails, reporting, and optimization.
      </p>

      <section className="docs-hero-shot" aria-label="Adgiro product screenshots">
        <img
          src={dashboardScreenshot}
          alt="Adgiro dashboard showing workspace metrics and optimization activity."
        />
        <div className="docs-hero-shot__thumbs">
          <img
            src={siteSegmentsScreenshot}
            alt="Adgiro site workspace showing segments and asset counts."
          />
          <img src={siteTrackingScreenshot} alt="Adgiro tracking workspace with event mappings." />
        </div>
      </section>

      <section className="docs-callout">
        <div>
          <div className="eyebrow">Best first look</div>
          <h3>The full launch path</h3>
          <p>
            Adgiro turns a website into campaign context, creative assets, Google and Meta launch
            plans, conversion tracking, lead reporting, and managed optimization.
          </p>
        </div>
        <Link href="/docs/first-session" className="button">
          Start guide
        </Link>
      </section>

      <section className="docs-grid">
        {topicLinks.map((link) => (
          <Link href={link.href} className="story-card docs-card docs-card--link" key={link.href}>
            <span className="docs-card__number">{link.eyebrow}</span>
            <h2>{link.label}</h2>
            <p>{topicIntro(link.href)}</p>
          </Link>
        ))}
      </section>
    </main>
  )
}

function DocsTopicPage({ topic }: { topic: DocsTopic }) {
  return (
    <main className="simple-page docs-page docs-page--article">
      <nav className="docs-topic-nav" aria-label="Docs sections">
        <Link href="/docs">Guide home</Link>
        {topicLinks.map((link) => (
          <Link
            href={link.href}
            className={link.href.endsWith(topic.slug) ? "active" : undefined}
            key={link.href}
          >
            {link.eyebrow}
          </Link>
        ))}
      </nav>

      <div className="eyebrow">{topic.eyebrow}</div>
      <h1>{topic.title}</h1>
      <p className="docs-intro">{topic.intro}</p>

      <figure className="docs-screenshot">
        <img src={topic.screenshot.src} alt={topic.screenshot.alt} />
        <figcaption>{topic.screenshot.caption}</figcaption>
      </figure>

      <div className="docs-article-stack">
        {topic.sections.map((section) => (
          <section key={section.title} className="story-card docs-card docs-card--article">
            <h2>{section.title}</h2>
            {section.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            <aside className="docs-example">
              <span>Example</span>
              <p>{section.example}</p>
            </aside>
          </section>
        ))}
      </div>
    </main>
  )
}

function topicIntro(href: string): string {
  if (href.endsWith("first-session"))
    return "How a buyer moves from site intake to campaign launch and lead reporting."
  if (href.endsWith("workspace"))
    return "How dashboards, sites, segments, accounts, and controls fit together."
  if (href.endsWith("site-setup"))
    return "How Adgiro turns website pages, media, and measurement into campaign context."
  if (href.endsWith("tracking"))
    return "How the website tag, events, mappings, and lead feed support optimization."
  if (href.endsWith("scale"))
    return "How the workspace grows from founder launch to team-managed governance."
  return "How campaign plans, creative, budgets, approvals, and optimization work."
}
